import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl, onPrepareHeadersBlob } from "@/redux/services/helpers";

const CHUNKED_UPLOAD_REDUCER_PATH = "chunkedUploadApi";

/**
 * Auth modes supported by the chunked upload service.
 *
 * All modes use the same base URL (/api/). The difference is the auth mechanism:
 *
 * | Mode            | Base URL | Auth mechanism               |
 * |-----------------|----------|------------------------------|
 * | "default"       | /api/    | Auth0 JWT + tenant headers   |
 * | "borrowerToken" | /api/    | X-Borrower-Token header      |
 * | "cookie"        | /api/    | Cookie session (credentials) |
 */
export const CHUNKED_UPLOAD_AUTH_MODES = {
  DEFAULT: "default",
  BORROWER_TOKEN: "borrowerToken",
  COOKIE: "cookie",
};

/**
 * Pre-built fetchBaseQuery instances per auth mode so we can reuse RTK Query's
 * built-in fetch handling while switching auth strategies at runtime.
 */
const defaultBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: onPrepareHeadersBlob,
});

const cookieBaseQuery = fetchBaseQuery({
  baseUrl,
  credentials: "include",
});

/**
 * Dynamic base query that reads `authMode` and `borrowerToken` from the
 * endpoint args, selects the right fetch strategy, and strips the extra
 * fields before forwarding to the underlying fetchBaseQuery.
 */
const dynamicBaseQuery = async (args, api, extraOptions) => {
  // `args` may be a string (GET shorthand) or an object
  const isObject = typeof args === "object" && args !== null;
  const authMode = (isObject && args._authMode) || CHUNKED_UPLOAD_AUTH_MODES.DEFAULT;
  const borrowerToken = (isObject && args._borrowerToken) || null;

  // Strip internal fields so they don't leak into the fetch request
  if (isObject) {
    // eslint-disable-next-line no-unused-vars
    const { _authMode, _borrowerToken, ...cleanArgs } = args;
    args = cleanArgs;
  }

  // --- Cookie mode: same base URL, but with credentials: "include" ---
  if (authMode === CHUNKED_UPLOAD_AUTH_MODES.COOKIE) {
    return cookieBaseQuery(args, api, extraOptions);
  }

  // --- Borrower-token mode: inject X-Borrower-Token header ---
  if (authMode === CHUNKED_UPLOAD_AUTH_MODES.BORROWER_TOKEN && borrowerToken) {
    const existingHeaders = (isObject && args.headers) || {};
    args = {
      ...(typeof args === "string" ? { url: args } : args),
      headers: {
        ...existingHeaders,
        "X-Borrower-Token": borrowerToken,
      },
    };
    return defaultBaseQuery(args, api, extraOptions);
  }

  // --- Default mode: Auth0 JWT + tenant headers ---
  return defaultBaseQuery(args, api, extraOptions);
};

export const chunkedUploadService = createApi({
  reducerPath: CHUNKED_UPLOAD_REDUCER_PATH,
  baseQuery: dynamicBaseQuery,
  endpoints: builder => ({
    initiateUpload: builder.mutation({
      query: ({ fileName, totalFileSize, totalChunks, contentType, authMode, borrowerToken }) => ({
        url: "uploads/initiate",
        method: "POST",
        body: { fileName, totalFileSize, totalChunks, contentType },
        headers: { "Content-Type": "application/json" },
        _authMode: authMode,
        _borrowerToken: borrowerToken,
      }),
    }),
    uploadChunk: builder.mutation({
      query: ({ uploadId, chunkIndex, formData, authMode, borrowerToken }) => ({
        url: `uploads/${uploadId}/chunks/${chunkIndex}`,
        method: "PUT",
        body: formData,
        _authMode: authMode,
        _borrowerToken: borrowerToken,
      }),
    }),
    getUploadProgress: builder.query({
      query: ({ uploadId, authMode, borrowerToken }) => ({
        url: `uploads/${uploadId}/progress`,
        _authMode: authMode,
        _borrowerToken: borrowerToken,
      }),
    }),
    abortUpload: builder.mutation({
      query: ({ uploadId, authMode, borrowerToken }) => ({
        url: `uploads/${uploadId}`,
        method: "DELETE",
        _authMode: authMode,
        _borrowerToken: borrowerToken,
      }),
    }),
  }),
});

export const {
  useInitiateUploadMutation,
  useUploadChunkMutation,
  useGetUploadProgressQuery,
  useLazyGetUploadProgressQuery,
  useAbortUploadMutation,
} = chunkedUploadService;
